import { useAuth, useClerk } from "@clerk/clerk-react";
import { type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAppAuth } from "../contexts/AuthContext";

/**
 * Shown when Clerk says yes and this app says no.
 *
 * ⚠ Sign-out is a BUTTON here and is never performed automatically. This used to call
 * `signOut()` from a `useEffect` on mount, which ends the Clerk session — and that session is
 * shared by every app on leychoon.app. Being unregistered *for the admin sync tool* says
 * nothing whatsoever about the other twelve, so arriving here dropped somebody out of every
 * tool they were entitled to use, to resolve a refusal in this one.
 *
 * It was also self-defeating on its own terms: the copy said "ask an admin to add you, then
 * sign in again", while the effect had already ended the session they would have signed in
 * with — the page described a state it had just destroyed.
 */
function AccessDenied({ title, message }: { title: string; message: string }) {
  const { signOut } = useClerk();
  return (
    <div className="center">
      <h2>{title}</h2>
      <p>{message}</p>
      <button type="button" onClick={() => void signOut()}>
        Sign out
      </button>
    </div>
  );
}

export function ProtectedRoute({
  children,
  requireAdmin = false,
}: {
  children: ReactNode;
  requireAdmin?: boolean;
}) {
  const { isLoaded, isSignedIn } = useAuth();
  const { appUser, loading } = useAppAuth();

  if (!isLoaded || loading) return <div className="center">Loading…</div>;
  if (!isSignedIn) return <Navigate to="/login" replace />;
  if (!appUser) return <div className="center">Checking access…</div>;

  if (appUser.status === "not_registered") {
    return (
      <AccessDenied
        title="Not registered"
        message="Your email isn’t on the guest list for this tool. Ask an admin to add you, then reload this page — you are still signed in, so nothing else needs setting up again."
      />
    );
  }
  if (appUser.status === "deactivated") {
    return (
      <AccessDenied
        title="Deactivated"
        message="Your access to this tool has been deactivated. Contact an admin if you think this is a mistake."
      />
    );
  }
  /**
   * ⚠ The `user` role no longer grants anything in this app — the API's only Layer-1 gate is
   * `requireAdmin`. This branch is what somebody holding it now sees, and it stays a plain
   * message rather than a sign-out: they are legitimately signed in, just not an admin here.
   */
  if (requireAdmin && appUser.role !== "admin") {
    return <div className="center">Admins only.</div>;
  }
  return <>{children}</>;
}
